import { Autocomplete, TextField } from '@mui/material';
import { api } from 'common';
import {
  canonicalCity,
  citiesEqual,
  getEnabledCityOptions,
} from 'common/sharedFunctions';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Selector de ciudad sincronizado con las ciudades habilitadas de /countries.
// Controlado: `value` es el identificador CANÓNICO de la ciudad (string, p. ej.
// "MEDELLIN-ANTIOQUIA") y `onChange` recibe el canónico elegido (o '' al limpiar).
// Si `value` trae una ciudad que ya no está habilitada o está guardada con otra
// grafía, se conserva (canonizada) como opción extra para no perder el dato.
const CitySelect = ({
  value,
  onChange,
  label = 'Ciudad',
  required = false,
  error = false,
  helperText,
  disabled = false,
  size,
}) => {
  const dispatch = useDispatch();
  const countriesdata = useSelector(state => state.countriesdata);
  const { fetchCountries } = api;

  useEffect(() => {
    if (!countriesdata || !countriesdata.countries) {
      dispatch(fetchCountries());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(
    () => getEnabledCityOptions(countriesdata),
    [countriesdata],
  );

  // Auto-canoniza el valor entrante: si un formulario carga "Cúcuta" (dato viejo)
  // se reescribe a "CUCUTA-..." en el estado del formulario, de modo que al
  // guardar sin tocar el campo igual queda normalizado.
  useEffect(() => {
    if (!value) return;
    const match = options.find(o => citiesEqual(o.value, value));
    const canonical = match ? match.value : canonicalCity(value);
    if (canonical && canonical !== value) {
      onChange(canonical);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value]);

  const multiCountry = useMemo(
    () => new Set(options.map(o => o.countryCode)).size > 1,
    [options],
  );

  const extraOption = useMemo(() => {
    if (!value) return null;
    if (options.some(o => citiesEqual(o.value, value))) return null;
    return {
      value: canonicalCity(value),
      country: 'Otras',
      countryCode: '',
    };
  }, [options, value]);

  const mergedOptions = useMemo(
    () => (extraOption ? [extraOption, ...options] : options),
    [extraOption, options],
  );

  const selected =
    mergedOptions.find(o => citiesEqual(o.value, value)) || extraOption || null;

  return (
    <Autocomplete
      fullWidth
      disablePortal
      disabled={disabled}
      options={mergedOptions}
      value={selected}
      onChange={(event, option) => onChange(option ? option.value : '')}
      groupBy={multiCountry ? option => option.country : undefined}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.value
      }
      isOptionEqualToValue={(option, val) =>
        citiesEqual(option.value, val && val.value ? val.value : val)
      }
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          size={size}
          error={!!error}
          helperText={
            error ? helperText || 'Este campo es obligatorio' : helperText
          }
        />
      )}
    />
  );
};

export default CitySelect;
